import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { merchantApi } from '../api/config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGauge, faUsers, faPlus, faWallet, faChartBar, faCalendarAlt,
  faRightFromBracket, faBars, faTimes, faUserShield,
  faSun, faMoon, faChevronLeft, faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { useDarkMode } from '../hooks/useDarkMode';

const ResellerLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [systemLogo, setSystemLogo] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  );

  const { t } = useTranslation();
  const { isDark, toggle: toggleDark } = useDarkMode();
  const resellerUser = JSON.parse(localStorage.getItem('reseller_user') || '{}');

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isDesktop) {
      setMobileMenuOpen(false);
      document.body.style.overflow = '';
      return;
    }

    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isDesktop, mobileMenuOpen]);

  const isCompactSidebar = isDesktop && sidebarCollapsed;

  const closeMobileMenu = () => {
    if (!isDesktop) {
      setMobileMenuOpen(false);
    }
  };

  const handleLogout = async () => {
    closeMobileMenu();
    try {
      await merchantApi.post('/auth/reseller/logout');
    } catch (error) {
      // ignore
    }
    localStorage.removeItem('reseller_user');
    navigate('/reseller/login');
  };

  useEffect(() => {
    loadSystemLogo();
  }, []);

  const loadSystemLogo = async () => {
    try {
      const response = await merchantApi.get('/admin/settings');
      const settings = response.data.data || [];
      const logoSetting = settings.find((s: any) => s.setting_key === 'system_logo_url');
      if (logoSetting && logoSetting.setting_value) {
        setSystemLogo(logoSetting.setting_value);
      }
    } catch (error) {
      // ignore
    }
  };

  const isSubReseller = !!resellerUser?.parent_reseller_id;

  const menuItems = [
    { path: '/reseller', icon: faGauge, label: 'Dashboard', exact: true },
    { path: '/reseller/merchants', icon: faUsers, label: 'Merchants' },
    { path: '/reseller/merchant-requests', icon: faPlus, label: 'Open Merchant' },
    ...(!isSubReseller ? [{ path: '/reseller/sub-reseller', icon: faUserShield, label: 'Sub-Resellers' }] : []),
    { path: '/reseller/wallet', icon: faWallet, label: 'Wallet' },
    { path: '/reseller/reports', icon: faChartBar, label: 'Reports' },
    { path: '/reseller/monthly-reports', icon: faCalendarAlt, label: 'Monthly Reports' },
  ];

  return (
    <div className="flex min-h-screen bg-slate-100 dark:bg-slate-900">
      <div
        className={`fixed inset-0 z-30 bg-slate-950/50 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          mobileMenuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={closeMobileMenu}
      />

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-40 flex w-72 flex-col bg-white shadow-2xl
          dark:bg-slate-800 dark:shadow-slate-900/60
          transition-transform duration-300 ease-in-out lg:transition-all lg:shadow-none
          lg:border-r lg:border-gray-200/70 dark:lg:border-slate-700/70
          ${isCompactSidebar ? 'lg:w-20' : 'lg:w-72'}
          ${mobileMenuOpen || isDesktop ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Floating collapse handle — Desktop only */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute -right-3 top-20 z-50 hidden lg:flex
                     h-6 w-6 items-center justify-center
                     rounded-full bg-white border border-gray-200 shadow-md
                     dark:bg-slate-700 dark:border-slate-600
                     text-gray-400 hover:text-gray-700 hover:border-gray-300 hover:shadow-lg hover:scale-110
                     dark:text-slate-400 dark:hover:text-slate-200 dark:hover:border-slate-500
                     transition-all duration-200"
          title={sidebarCollapsed ? 'ขยาย sidebar' : 'ย่อ sidebar'}
        >
          <FontAwesomeIcon icon={sidebarCollapsed ? faChevronRight : faChevronLeft} className="text-[8px]" />
        </button>
        {/* Top gradient accent strip */}
        <div className="h-[3px] bg-gradient-primary flex-shrink-0" />

        {/* Header / Logo Section */}
        <div className={`relative px-4 pt-5 pb-4 border-b border-gray-100 dark:border-slate-700 ${isCompactSidebar ? 'text-center' : ''}`}>
          {/* Close - Mobile */}
          <button
            type="button"
            onClick={closeMobileMenu}
            className="absolute top-[18px] right-3 flex h-7 w-7 items-center justify-center rounded-lg
                       text-gray-400 hover:text-gray-600 hover:bg-gray-100
                       dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700
                       transition-all duration-200 lg:hidden"
            aria-label="Close navigation menu"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xs" />
          </button>

          {!isCompactSidebar ? (
            <>
              {systemLogo ? (
                <img
                  src={systemLogo}
                  alt="Logo"
                  className="max-h-16 w-auto max-w-full object-contain mb-4"
                />
              ) : (
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-white"
                    style={{ background: 'var(--theme-primary-gradient)', boxShadow: 'var(--theme-shadow-primary)' }}
                  >
                    <span className="font-bold text-sm">R</span>
                  </div>
                  <div>
                    <h1 className="text-gray-800 dark:text-slate-100 font-semibold text-[15px] tracking-tight leading-none">Reseller</h1>
                    <p className="text-[9px] font-medium uppercase tracking-[0.15em] mt-[3px] text-gray-400 dark:text-slate-500">
                      Portal
                    </p>
                  </div>
                </div>
              )}

              {/* User Info Card */}
              <div className="rounded-xl overflow-hidden bg-gray-50 border border-gray-100/80 dark:bg-slate-700/50 dark:border-slate-700/80">
                <div className="h-[2px] bg-gradient-primary" />
                <div className="p-2.5">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-semibold text-xs flex-shrink-0"
                      style={{ background: 'var(--theme-primary-gradient)', boxShadow: 'var(--theme-shadow-primary)' }}
                    >
                      {(resellerUser.name || 'R').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate leading-none mb-[5px] text-gray-700 dark:text-slate-200">
                        {resellerUser.name || 'Reseller'}
                      </p>
                      <p className="text-[10px] flex items-center gap-1.5 leading-none text-gray-400 dark:text-slate-500">
                        <span className="w-1.5 h-1.5 rounded-full animate-pulse flex-shrink-0" style={{ backgroundColor: '#10b981' }} />
                        P.In: {resellerUser.fee_rate || 0}% · P.Out: {resellerUser.payout_fee_rate || 0}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div
              className="w-9 h-9 mx-auto rounded-xl flex items-center justify-center text-white mt-5"
              style={{ background: 'var(--theme-primary-gradient)', boxShadow: 'var(--theme-shadow-primary)' }}
            >
              <span className="font-bold text-sm">R</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav
          className={`flex-1 py-3 overflow-y-auto ${isCompactSidebar ? 'px-1' : 'px-2.5 custom-scrollbar-light'}`}
          style={isCompactSidebar ? { scrollbarWidth: 'none' } : undefined}
        >
          <div className="space-y-[2px]">
            {menuItems.map((item) => {
              const isActive = item.exact
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  title={isCompactSidebar ? item.label : undefined}
                  onClick={closeMobileMenu}
                  className={`
                    flex items-center gap-3 py-2.5 rounded-xl text-sm relative group
                    transition-all duration-200
                    ${isCompactSidebar ? 'justify-center px-0' : 'pl-3.5 pr-3'}
                    ${isActive
                      ? 'font-medium text-white'
                      : 'font-normal text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700/50'
                    }
                  `}
                  style={isActive ? {
                    background: 'var(--theme-primary-gradient)',
                    boxShadow: 'var(--theme-shadow-primary)',
                  } : undefined}
                >
                  {/* Icon */}
                  <span className={`text-[13px] flex-shrink-0 transition-colors duration-200 ${
                    isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-500 dark:text-slate-500 dark:group-hover:text-slate-400'
                  }`}>
                    <FontAwesomeIcon icon={item.icon} />
                  </span>

                  {!isCompactSidebar && (
                    <span className="truncate">{item.label}</span>
                  )}

                  {/* Compact mode tooltip */}
                  {isCompactSidebar && (
                    <div
                      className="absolute left-full ml-3 px-3 py-1.5 text-gray-700 bg-white text-xs rounded-lg
                                 dark:text-slate-200 dark:bg-slate-700 dark:border-slate-600
                                 opacity-0 invisible group-hover:opacity-100 group-hover:visible
                                 transition-all duration-200 whitespace-nowrap z-50 shadow-lg border border-gray-200"
                    >
                      {item.label}
                      <div className={`absolute left-0 top-1/2 -translate-x-[7px] -translate-y-1/2 border-[5px] border-transparent ${isDark ? 'border-r-slate-700' : 'border-r-white'}`} />
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Logout Section */}
        <div className="px-2.5 pb-3 pt-2.5 border-t border-gray-100 dark:border-slate-700">
          <button
            onClick={handleLogout}
            title={isCompactSidebar ? t('common.logout') : undefined}
            className={`
              w-full py-2.5 px-3.5 rounded-xl text-sm font-medium
              flex items-center gap-3 transition-all duration-200 cursor-pointer
              border border-gray-200 bg-gray-50 text-gray-400
              hover:text-red-500 hover:bg-red-50 hover:border-red-200
              dark:border-slate-600 dark:bg-slate-700/30 dark:text-slate-500
              dark:hover:text-red-400 dark:hover:bg-red-900/20 dark:hover:border-red-800
              ${isCompactSidebar ? 'justify-center' : ''}
            `}
          >
            <FontAwesomeIcon icon={faRightFromBracket} />
            {!isCompactSidebar && <span>{t('common.logout')}</span>}
          </button>

          {/* Version */}
          {!isCompactSidebar && (
            <p className="text-center text-[9.5px] mt-2.5 tracking-wide text-gray-300 dark:text-slate-700">
              Reseller Portal v2.0
            </p>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className={`
        ml-0 flex-1 min-w-0 min-h-screen overflow-x-clip transition-all duration-300
        ${isCompactSidebar ? 'lg:ml-20' : 'lg:ml-72'}
      `}>
        {/* Top Header Bar — ใช้ overflow-x-clip บน main (ไม่ใช่ hidden) ไม่งั้น sticky ไม่ยึด viewport */}
        <div className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-gray-200/60 bg-white/80 px-4 py-3 backdrop-blur-md sm:px-6 dark:border-slate-700/60 dark:bg-slate-800/80">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200/70 text-gray-600 transition-colors hover:bg-gray-50 lg:hidden dark:border-slate-700/70 dark:text-slate-400 dark:hover:bg-slate-700/50"
            aria-label="Open navigation menu"
          >
            <FontAwesomeIcon icon={faBars} />
          </button>

          <div className="flex items-center gap-2 ml-auto">
            {/* Dark mode toggle */}
            <button
              type="button"
              onClick={toggleDark}
              title={isDark ? 'เปลี่ยนเป็น Light Mode' : 'เปลี่ยนเป็น Dark Mode'}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200/70 text-gray-500 transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 dark:border-slate-700/70 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
            >
              <FontAwesomeIcon icon={isDark ? faSun : faMoon} className="text-sm" />
            </button>
            <LanguageSwitcher />
          </div>
        </div>

        <div className="p-4 sm:p-6 min-h-[calc(100vh-57px)]">
          <Outlet />
        </div>
      </main>

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar-light::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar-light::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar-light::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }
        .custom-scrollbar-light::-webkit-scrollbar-thumb:hover { background: #d1d5db; }
      `}</style>
    </div>
  );
};

export default ResellerLayout;
